import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Snackbar, Alert, Tooltip, Chip, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions, TextField,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import api from '../services/api';
import { getDefaultLayout, CABINS } from '../config/floorLayout';
import './FloorPlan.css';


const FloorLayoutEditor = () => {
  const [layout, setLayout] = useState(getDefaultLayout());
  const [seatMap, setSeatMap] = useState({});
  const mode = 'reserve';
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [reserveDialog, setReserveDialog] = useState({ open: false, seatNumber: null, isFixed: false });
  const [reserveLabel, setReserveLabel] = useState('');
  const [dragSeat, setDragSeat] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [seatsRes, gridRes] = await Promise.all([
        api.get('/seats'),
        api.get('/seat-layout/grid'),
      ]);

      const map = {};
      seatsRes.data.forEach(s => { map[s.seat_number] = s; });
      setSeatMap(map);

      if (gridRes.data) {
        setLayout(gridRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isSeatFixed = (seatNumber) => seatMap[seatNumber]?.is_fixed || false;

  // --- EDITOR MODE: Drag & Drop to swap seats ---
  const handleDragStart = (e, seatNumber) => {
    if (mode !== 'editor' || seatNumber === null) return;
    setDragSeat(seatNumber);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(seatNumber));
  };

  const handleDragOver = (e, seatNumber) => {
    if (mode !== 'editor' || dragSeat === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(seatNumber);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = (e, targetSeat) => {
    e.preventDefault();
    if (mode !== 'editor' || dragSeat === null || dragSeat === targetSeat) {
      setDragSeat(null);
      setDropTarget(null);
      return;
    }

    // Swap the two seat numbers in the layout
    const newLayout = JSON.parse(JSON.stringify(layout));
    const zones = ['zoneAUpper', 'zoneALower', 'zoneB', 'zoneCUpper', 'zoneCLower'];

    let dragPos = null;
    let dropPos = null;

    for (const zoneKey of zones) {
      for (let r = 0; r < newLayout[zoneKey].length; r++) {
        for (let c = 0; c < newLayout[zoneKey][r].length; c++) {
          if (newLayout[zoneKey][r][c] === dragSeat) dragPos = { zone: zoneKey, r, c };
          if (newLayout[zoneKey][r][c] === targetSeat) dropPos = { zone: zoneKey, r, c };
        }
      }
    }

    if (dragPos && dropPos) {
      newLayout[dragPos.zone][dragPos.r][dragPos.c] = targetSeat;
      newLayout[dropPos.zone][dropPos.r][dropPos.c] = dragSeat;
      setLayout(newLayout);
      setSnackbar({
        open: true,
        message: `Swapped seat ${dragSeat} with seat ${targetSeat === null ? 'empty' : targetSeat}`,
        severity: 'info',
      });
    }

    setDragSeat(null);
    setDropTarget(null);
  };

  const handleDragEnd = () => {
    setDragSeat(null);
    setDropTarget(null);
  };

  // --- RESERVE MODE ---
  const handleSeatClick = (seatNumber) => {
    if (mode !== 'reserve' || seatNumber === null) return;
    const seat = seatMap[seatNumber];
    setReserveDialog({
      open: true,
      seatNumber,
      isFixed: seat?.is_fixed || false,
    });
    setReserveLabel(seat?.fixed_label || '');
  };

  const handleReserve = async () => {
    const { seatNumber, isFixed } = reserveDialog;
    if (!seatNumber) return;
    try {
      await api.put('/seat-layout/reserve', {
        seat_numbers: [seatNumber],
        is_fixed: !isFixed,
        fixed_label: !isFixed ? (reserveLabel || 'Admin Reserved') : null,
      });
      setSnackbar({
        open: true,
        message: isFixed ? `Seat ${seatNumber} unreserved` : `Seat ${seatNumber} reserved`,
        severity: 'success',
      });
      setReserveDialog({ open: false, seatNumber: null, isFixed: false });
      setReserveLabel('');
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update seat.', severity: 'error' });
    }
  };

  const fixedCount = Object.values(seatMap).filter(s => s.is_fixed).length;
  const totalCount = Object.keys(seatMap).length;

  const renderSeat = (seatNumber) => {
    if (seatNumber === null) {
      // Empty cells are drop targets in editor mode
      if (mode === 'editor') {
        return (
          <div
            className={`seat-empty ${dropTarget === null && dragSeat ? 'seat-drop-hint' : ''}`}
            key={`empty-${Math.random()}`}
            style={{ border: '2px dashed #ccc', borderRadius: 6 }}
            onDragOver={(e) => { e.preventDefault(); }}
          />
        );
      }
      return <div className="seat-empty" key={`empty-${Math.random()}`} />;
    }

    const isFixed = isSeatFixed(seatNumber);
    const isDragging = dragSeat === seatNumber;
    const isDropTarget = dropTarget === seatNumber;

    let seatClass = 'seat';
    let seatStyle = {};

    if (isFixed) {
      seatClass += ' seat-fixed';
    } else {
      seatClass += ' seat-available';
    }

    if (mode === 'editor') {
      seatStyle.cursor = 'grab';
      if (isDragging) {
        seatStyle.opacity = 0.4;
        seatStyle.transform = 'scale(0.9)';
      }
      if (isDropTarget && dragSeat !== null) {
        seatStyle.border = '3px solid #68DA6A';
        seatStyle.boxShadow = '0 0 12px rgba(104, 218, 106, 0.6)';
        seatStyle.transform = 'scale(1.15)';
      }
    } else {
      seatStyle.cursor = 'pointer';
    }

    const tooltipTitle = mode === 'editor'
      ? `Seat ${seatNumber}${isFixed ? ' (Fixed)' : ''} - Drag to swap position`
      : `Seat ${seatNumber}${isFixed ? ' (Fixed) - Click to unreserve' : ' - Click to reserve'}`;

    return (
      <Tooltip title={tooltipTitle} arrow key={seatNumber}>
        <div
          className={seatClass}
          style={seatStyle}
          draggable={mode === 'editor'}
          onDragStart={(e) => handleDragStart(e, seatNumber)}
          onDragOver={(e) => handleDragOver(e, seatNumber)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, seatNumber)}
          onDragEnd={handleDragEnd}
          onClick={() => handleSeatClick(seatNumber)}
        >
          {isFixed && mode === 'reserve' ? (
            <>
              <LockIcon sx={{ fontSize: 11 }} />
              <span style={{ fontSize: 7, position: 'absolute', bottom: 1 }}>{seatNumber}</span>
            </>
          ) : seatNumber}
        </div>
      </Tooltip>
    );
  };

  const renderSeatRow = (seats, keyPrefix) => (
    <div className="seat-row" key={keyPrefix}>
      {seats.map((seat) => renderSeat(seat))}
    </div>
  );

  const renderZone = (rows, keyPrefix) => (
    <div className="zone-block" key={keyPrefix}>
      {rows.map((row, idx) => renderSeatRow(row, `${keyPrefix}-row-${idx}`))}
    </div>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={600} sx={{ color: '#052e2b' }}>
          Floor Layout Settings
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label={`${totalCount} seats`} variant="outlined" />
          <Chip label={`${fixedCount} fixed`} sx={{ backgroundColor: '#e0e0e0' }} />
          <Chip label={`${totalCount - fixedCount} available`} sx={{ backgroundColor: '#d4f5d0', color: '#052e2b' }} />
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon sx={{ color: '#052e2b', fontSize: 20 }} />
          <Typography variant="body2" color="text.secondary">
            Click any seat to <strong>reserve</strong> (block) or <strong>unreserve</strong> it. Reserved seats cannot be booked by team leaders.
          </Typography>
        </Box>
      </Paper>

      {/* Floor Plan - identical structure to FloorPlan.js */}
      <Paper sx={{ p: 3, overflowX: 'auto' }}>
        <div className="floor-plan-wrapper">
          <div className="floor-plan-main">
            <div className="floor-upper">
              <div className="zone-container">
                <Typography variant="subtitle2" className="zone-label" sx={{ mb: 1, fontWeight: 700, color: '#052e2b' }}>
                  Zone C (114-181)
                </Typography>
                {renderZone(layout.zoneAUpper, 'zone-a-upper')}
              </div>
              <div className="rooms-middle">
                <div className="rooms-strip">
                  <div className="room-block server-room">
                    <Typography variant="caption" fontWeight={600}>Server Room</Typography>
                  </div>
                  <div className="room-block meeting-room">
                    <Typography variant="caption" fontWeight={600}>Ravi</Typography>
                  </div>
                  <div className="room-block meeting-room">
                    <Typography variant="caption" fontWeight={600}>Sutlej</Typography>
                  </div>
                  <div className="room-block meeting-room">
                    <Typography variant="caption" fontWeight={600}>Saraswati</Typography>
                  </div>
                  <div className="room-block entry-block">
                    <Typography variant="caption" fontWeight={700}>Entry to<br/>the floor</Typography>
                  </div>
                </div>
              </div>
              <div className="zone-container">
                <Typography variant="subtitle2" className="zone-label" sx={{ mb: 1, fontWeight: 700, color: '#052e2b' }}>
                  Zone A (1-74)
                </Typography>
                <div className="zone-c-upper-spacer" />
                {renderZone(layout.zoneCUpper, 'zone-c-upper')}
              </div>
            </div>
            <div className="corridor">
              <Typography variant="caption" sx={{ color: '#999', letterSpacing: 4, textTransform: 'uppercase' }}>
                Corridor
              </Typography>
            </div>
            <div className="floor-lower">
              <div className="zone-container">
                {renderZone(layout.zoneALower, 'zone-a-lower')}
              </div>
              <div className="zone-container zone-b-container">
                {renderZone(layout.zoneB, 'zone-b')}
                <Typography variant="subtitle2" className="zone-label" sx={{ mt: 1, fontWeight: 700, color: '#052e2b' }}>
                  Zone B (75-113)
                </Typography>
              </div>
              <div className="zone-container">
                {renderZone(layout.zoneCLower, 'zone-c-lower')}
              </div>
            </div>
          </div>
          <div className="cabins-column">
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#052e2b' }}>
              Cabins (Fixed)
            </Typography>
            {CABINS.map((cabin) => (
              <div className="cabin-block" key={cabin.name}>
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                  {cabin.name}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </Paper>

      {/* Reserve dialog */}
      <Dialog open={reserveDialog.open} onClose={() => setReserveDialog({ open: false, seatNumber: null, isFixed: false })}>
        <DialogTitle>
          {reserveDialog.isFixed
            ? <><LockOpenIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Unreserve Seat {reserveDialog.seatNumber}</>
            : <><LockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Reserve Seat {reserveDialog.seatNumber}</>
          }
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {reserveDialog.isFixed
              ? 'This seat is currently reserved. Unreserve it to make it bookable?'
              : 'Reserve this seat? It will become fixed and unbookable by team leaders.'}
          </DialogContentText>
          {!reserveDialog.isFixed && (
            <TextField
              label="Label (optional)"
              fullWidth size="small"
              value={reserveLabel}
              onChange={(e) => setReserveLabel(e.target.value)}
              placeholder="e.g. IT Equipment, Reserved for VP"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReserveDialog({ open: false, seatNumber: null, isFixed: false })}>Cancel</Button>
          <Button onClick={handleReserve} variant="contained" sx={{ backgroundColor: '#052e2b' }}>
            {reserveDialog.isFixed ? 'Unreserve' : 'Reserve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FloorLayoutEditor;
